import React, { useState, useCallback, useEffect, useRef } from "react";
import {
    Plus, Search, Trash2, Users, School,
    ChevronLeft, ChevronRight, LayoutGrid, CheckCircle2,
    Loader2, AlertCircle, CalendarDays, ClipboardList, MoveRight,
} from "lucide-react";
import Cookies from "js-cookie";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TeacherSelectionDialog } from "./TeacherSelectionDioalogBox";
import { TimetableEditor, TimetableGrid, createEmptyGrid } from "./TimetableEditor";
import { TimetableConflictDialog, type TimetableConflictEntry } from "./TimetableConflictDialog";
import { ClassroomRoster } from "./ClassroomRoster";
import { ClassroomRegistery } from "./ClassroomRegistery";
import { WebService } from "@/service/webService";
import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";
import { useInstituteLabels } from "@/lib/instituteLabels";
import { useStructure, StructurePicker, MoveClassroomDialog, BulkAssignDialog } from "./ClassroomStructure";
import { SearchableSelect } from "@/components/SearchableSelect";
import { RowActions } from "@/components/RowActions";

// ── Types ─────────────────────────────────────────────────────────────────────
type Coordinator = {
    uid: string;
    name: string;
    employee_id: string;
    email: string;
    profile_pic: string | null;
};

type Classroom = {
    id: string;
    name: string;
    programme: string;
    level: string;
    grade: string;
    coordinator: Coordinator | null;
    studentCount: number;
    created_at?: number;
    timetable?: TimetableGrid | null;
    departmentName: string | null;   // campus structure link (null = unassigned)
    programmeId: string | null;
};

const emptyForm: Omit<Classroom, 'id' | 'studentCount'> = {
    name: '',
    programme: '',
    level: '',
    grade: '',
    coordinator: null,
    departmentName: null,
    programmeId: null,
};

// List filter: everything, classrooms with no department yet, or one department
const ALL = '__all__';

// Bulk "Assign…" (tick rows, or by old programme text) is switched off for
// now: production has few classrooms, and Move on each row covers them.
// Flip to true to bring back the button and the row checkboxes.
const SHOW_BULK_ASSIGN = false;
const UNASSIGNED = '__unassigned__';

const webService = new WebService();

function generateId(): string {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ClassroomManagementPage() {
    // List state
    const L = useInstituteLabels();
    const [ classrooms, setClassrooms ] = useState<Classroom[]>([]);
    const [ isLoadingList, setIsLoadingList ] = useState(false);
    const [ listError, setListError ] = useState<string | null>(null);
    const [ search, setSearch ] = useState('');
    const [ currentPage, setCurrentPage ] = useState(1);
    const [ lastPageSnap, setLastPageSnap ] = useState<string | null>(null);
    const [ hasMore, setHasMore ] = useState(true);
    const PAGE_SIZE = 10;

    // Campus structure (departments / programmes). available=false means the
    // server has no structure routes yet: the page then works as before.
    const structure = useStructure();
    const hasStructure = structure.available === true;
    const [ filter, setFilter ] = useState<string>(ALL);
    // Programme filter — only within a chosen department (ALL = every programme of it)
    const [ programmeFilter, setProgrammeFilter ] = useState<string>(ALL);
    const departmentProgrammes = (filter === ALL || filter === UNASSIGNED)
        ? []
        : structure.programmes.filter(p => p.departmentName === filter).sort((a, b) => a.name.localeCompare(b.name));
    const [ selectedIds, setSelectedIds ] = useState<Set<string>>(new Set());
    const [ moveTarget, setMoveTarget ] = useState<Classroom | null>(null);
    const [ bulkOpen, setBulkOpen ] = useState(false);

    // Creating / editing classrooms is admin-tier (server: CASL classroom)
    const canManage = [ 'superadmin', 'admin', 'subadmin' ].includes(Cookies.get('role') || '');

    // Dialogs
    const [ createOpen, setCreateOpen ] = useState(false);
    const [ viewStudentsOpen, setViewStudentsOpen ] = useState(false);
    const [ registeryOpen, setRegisteryOpen ] = useState(false);
    const [ timetableOpen, setTimetableOpen ] = useState(false);
    const [ selectedClass, setSelectedClass ] = useState<Classroom | null>(null);
    const [ timetableGrid, setTimetableGrid ] = useState<TimetableGrid | null>(null);

    // Form
    const [ form, setForm ] = useState<typeof emptyForm>(emptyForm);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const [ formError, setFormError ] = useState<string | null>(null);

    // Timetable saving
    const [ isSavingTT, setIsSavingTT ] = useState(false);
    // Set only when updateTimetable responds 409 (a teacher in this grid is
    // already assigned elsewhere at an overlapping time) — the pending grid
    // is remembered so "Proceed Anyway" can resubmit the exact same save
    // with force:true rather than the admin having to redo any edits.
    const [ conflictReport, setConflictReport ] = useState<TimetableConflictEntry[] | null>(null);
    const [ pendingTimetableGrid, setPendingTimetableGrid ] = useState<TimetableGrid | null>(null);

    // Misc
    const [ deletingId, setDeletingId ] = useState<string | null>(null);
    const [ deleteTarget, setDeleteTarget ] = useState<Classroom | null>(null);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    // Use a ref to always have the latest search/page values inside the fetch
    // without needing them as dependencies that cause double-fires.
    const searchRef = useRef(search);
    const currentPageRef = useRef(currentPage);
    const lastPageSnapRef = useRef(lastPageSnap);
    const filterRef = useRef(filter);
    useEffect(() => { filterRef.current = filter; }, [ filter ]);
    const programmeFilterRef = useRef(programmeFilter);
    useEffect(() => { programmeFilterRef.current = programmeFilter; }, [ programmeFilter ]);

    useEffect(() => { searchRef.current = search; }, [ search ]);
    useEffect(() => { currentPageRef.current = currentPage; }, [ currentPage ]);
    useEffect(() => { lastPageSnapRef.current = lastPageSnap; }, [ lastPageSnap ]);

    const fetchClassrooms = useCallback(async (reset = false) => {
        setIsLoadingList(true);
        setListError(null);
        try {
            //if (!webService.ins_code || !webService.authToken) {
            //    throw new Error('Session missing');
            //}

            const page = reset ? 1 : currentPageRef.current;
            const snap = reset ? null : lastPageSnapRef.current;

            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(PAGE_SIZE),
                query: searchRef.current,
                ...(snap ? { lastDoc: snap } : {}),
                ...(filterRef.current === UNASSIGNED ? { unassigned: 'true' } : {}),
                ...(filterRef.current !== ALL && filterRef.current !== UNASSIGNED ? { departmentName: filterRef.current } : {}),
                ...(programmeFilterRef.current !== ALL ? { programmeId: programmeFilterRef.current } : {}),
            });

            const res = await fetch(
                `${environment.BASE_URL}/classrooms/getClassRooms?${params}`,
                { headers: apiHeaders() }
            );
            const data = await res.json();

            if (data.success) {
                const mapped: Classroom[] = (data.classrooms || []).map((c: any) => {
                    let timetable: TimetableGrid | null = null;
                    try {
                        if (c.timetable) {
                            timetable = typeof c.timetable === 'string'
                                ? JSON.parse(c.timetable)
                                : c.timetable;
                        }
                    } catch (e) {
                        console.error('Failed to parse timetable:', e);
                    }

                    return {
                        id: c.id,
                        name: c.name,
                        programme: c.programme || '',
                        level: c.level || '',
                        grade: c.grade || '',
                        coordinator: c.incharge_uid ? {
                            uid: c.incharge_uid,
                            name: c.incharge_name || '',
                            employee_id: c.incharge_eid || '',
                            email: c.incharge_email || '',
                            profile_pic: null,
                        } : null,
                        studentCount: 0,
                        created_at: c.created_at,
                        timetable,
                        departmentName: c.department_name || null,
                        programmeId: c.programme_id || null,
                    };
                });

                setClassrooms(reset ? mapped : prev => [ ...prev, ...mapped ]);
                setHasMore(data.hasMore || false);
                if (!reset && data.lastDoc) setLastPageSnap(data.lastDoc);
            } else {
                setListError(data.message || 'Failed to load classrooms');
            }
        } catch (e: any) {
            setListError(e.message);
        } finally {
            setIsLoadingList(false);
        }
    }, []); // stable — no deps that change

    // ── Mount: initial load ───────────────────────────────────────────────────
    // Runs once on mount (tab switch causes remount if tabs unmount children).
    useEffect(() => {
        fetchClassrooms(true);
    }, [ fetchClassrooms ]);

    // ── Page Visibility: re-fetch when user switches back to this browser tab ─
    // Handles the case where the OS tab (browser) is switched, not just your
    // app's internal tab. Remove if you don't want a refresh on every tab focus.
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                fetchClassrooms(true);
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [ fetchClassrooms ]);

    // ── Search: debounced re-fetch ────────────────────────────────────────────
    // Skips on the very first render (initial load already handled above).
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const t = setTimeout(() => {
            setCurrentPage(1);
            setLastPageSnap(null);
            fetchClassrooms(true);
        }, 400);
        return () => clearTimeout(t);
    }, [ search, fetchClassrooms ]);

    const isFirstFilter = useRef(true);
    useEffect(() => {
        if (isFirstFilter.current) { isFirstFilter.current = false; return; }
        setCurrentPage(1);
        setLastPageSnap(null);
        setSelectedIds(new Set());
        fetchClassrooms(true);
    }, [ filter, programmeFilter, fetchClassrooms ]);

    const toggleSelected = (id: string) => setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // ── Create ────────────────────────────────────────────────────────────────
    const handlePublish = async () => {
        if (!form.name.trim()) { setFormError('Classroom name is required'); return; }
        if (!form.coordinator) { setFormError('Please assign a coordinator'); return; }
        if (hasStructure && structure.departments.length > 0 && !form.departmentName) {
            setFormError(`Please choose a ${L.department.toLowerCase()}`); return;
        }

        setIsSubmitting(true);
        setFormError(null);

        try {
            const classRoomId = generateId();
            const res = await fetch(
                `${environment.BASE_URL}/classrooms/manageClassRoom`,
                {
                    method: 'POST',
                    headers: apiHeaders(),
                    body: JSON.stringify({
                        classRoomId,
                        name: form.name,
                        programme: form.programme,
                        ...(form.departmentName ? { departmentName: form.departmentName } : {}),
                        ...(form.programmeId ? { programmeId: form.programmeId } : {}),
                        level: form.level,
                        grade: form.grade,
                        incharge: {
                            uid: form.coordinator.uid,
                            name: form.coordinator.name,
                            eid: form.coordinator.employee_id,
                            email: form.coordinator.email,
                        },
                    }),
                }
            );
            const data = await res.json();

            if (data.success) {
                setClassrooms(prev => [ {
                    id: classRoomId,
                    name: form.name,
                    programme: form.programmeId ? structure.programmeLabel(form.programmeId) : form.programme,
                    level: form.level,
                    grade: form.grade,
                    coordinator: form.coordinator,
                    studentCount: 0,
                    created_at: Date.now(),
                    departmentName: form.departmentName,
                    programmeId: form.programmeId,
                }, ...prev ]);
                setCreateOpen(false);
                setForm(emptyForm);
            } else {
                setFormError(data.message === 'PROGRAMME_ALREADY_HAS_CLASSROOM'
                    ? `That ${L.programme.toLowerCase()} already has its ${L.classroom.toLowerCase()}.`
                    : (data.message || 'Failed to create classroom'));
            }
        } catch (e: any) {
            setFormError(e.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (classRoomId: string) => {
        setDeletingId(classRoomId);
        try {
            const res = await fetch(
                `${environment.BASE_URL}/classrooms/manageClassRoom`,
                {
                    method: 'DELETE',
                    headers: apiHeaders(),
                    body: JSON.stringify({ classRoomId }),
                }
            );
            const data = await res.json();
            if (data.success) {
                setClassrooms(prev => prev.filter(c => c.id !== classRoomId));
            } else {
                console.error('Delete failed:', data.message);
            }
        } catch (e) {
            console.error('Delete error:', e);
        } finally {
            setDeletingId(null);
        }
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        setDeleteTarget(null);
        handleDelete(target.id);
    };

    // ── Timetable ─────────────────────────────────────────────────────────────
    const openTimetable = (classroom: Classroom) => {
        setSelectedClass(classroom);
        setTimetableGrid(classroom.timetable || null);
        setTimetableOpen(true);
    };

    const handleSaveTimetable = async (grid: TimetableGrid, force = false) => {
        if (!selectedClass) return;
        setIsSavingTT(true);
        try {
            const res = await fetch(
                `${environment.BASE_URL}/classrooms/updateTimetable`,
                {
                    method: 'POST',
                    headers: apiHeaders(),
                    body: JSON.stringify({
                        classRoomId: selectedClass.id,
                        timetable: grid,
                        force,
                    }),
                }
            );
            const data = await res.json();

            if (res.status === 409 && data.conflict) {
                setConflictReport(data.conflicts || []);
                setPendingTimetableGrid(grid);
                return; // editor stays open behind the conflict dialog
            }

            if (data.success) {
                setConflictReport(null);
                setPendingTimetableGrid(null);
                setTimetableOpen(false);
            } else {
                console.error('Timetable save failed:', data.message);
            }
        } catch (e) {
            console.error('Timetable save error:', e);
        } finally {
            setIsSavingTT(false);
        }
    };

    const handleProceedAnyway = () => {
        if (!pendingTimetableGrid) return;
        handleSaveTimetable(pendingTimetableGrid, true);
    };

    const handleCancelConflict = () => {
        setConflictReport(null);
        setPendingTimetableGrid(null);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <TooltipProvider>
            <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8 text-foreground font-sans">

                {/* HEADER */}
                <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                            <School className="h-6 w-6 text-primary" /> {L.classroom} Management
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Manage institutional academic units and student rosters
                        </p>
                    </div>
                    {canManage && (
                        <div className="flex gap-2 shrink-0">
                            {SHOW_BULK_ASSIGN && hasStructure && (
                                <Button variant="outline" className="gap-2" onClick={() => setBulkOpen(true)}>
                                    <MoveRight className="h-4 w-4" /> Assign{selectedIds.size ? ` (${selectedIds.size})` : ''}…
                                </Button>
                            )}
                            <Button
                                onClick={() => { setForm(emptyForm); setFormError(null); setCreateOpen(true); }}
                                className="gap-2 shadow-lg shadow-primary/20"
                            >
                                <Plus className="h-4 w-4" /> New {L.classroom}
                            </Button>
                        </div>
                    )}
                </div>

                {/* STATS */}
                <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {[
                        { icon: LayoutGrid, label: 'Total Classes', value: classrooms.length, color: 'bg-primary/10 text-primary' },
                        { icon: CheckCircle2, label: 'With Coordinator', value: classrooms.filter(c => c.coordinator).length, color: 'bg-emerald-500/10 text-emerald-600' },
                    ].map(({ icon: Icon, label, value, color }) => (
                        <Card key={label} className="border border-border/60 shadow-sm">
                            <CardContent className="flex items-center gap-4 p-5">
                                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
                                    <p className="text-2xl font-bold">{value}</p>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* TABLE */}
                <Card className="border border-border/60 shadow-sm overflow-hidden bg-card">
                    <CardHeader className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-center sm:justify-between border-b border-border/40 bg-muted/5">
                        <div>
                            <CardTitle className="text-base font-semibold">Institutional {L.classrooms}</CardTitle>
                            <CardDescription className="text-sm">Live database of current classrooms</CardDescription>
                        </div>
                        <div className="flex w-full sm:w-auto gap-2">
                        {hasStructure && (
                            <div className="w-full sm:w-52">
                                <SearchableSelect
                                    value={filter === ALL ? null : filter}
                                    nullLabel={`All ${L.departments.toLowerCase()}`}
                                    searchPlaceholder={`Search ${L.departments.toLowerCase()}…`}
                                    emptyText={`No ${L.departments.toLowerCase()} found.`}
                                    options={[
                                        { value: UNASSIGNED, label: "Unassigned", hint: `${L.classrooms} with no ${L.department.toLowerCase()} yet` },
                                        ...structure.departments.map(d => ({ value: d.departmentName, label: structure.departmentLabel(d.departmentName) })),
                                    ]}
                                    onChange={(v) => {
                                        // a new department starts on all of its programmes; the
                                        // ref is set now so the refetch below already sees it
                                        programmeFilterRef.current = ALL;
                                        setProgrammeFilter(ALL);
                                        setFilter(v ?? ALL);
                                    }}
                                />
                            </div>
                        )}
                        {hasStructure && departmentProgrammes.length > 0 && (
                            <div className="w-full sm:w-48">
                                <SearchableSelect
                                    value={programmeFilter === ALL ? null : programmeFilter}
                                    nullLabel={`All ${L.programmes.toLowerCase()}`}
                                    searchPlaceholder={`Search ${L.programmes.toLowerCase()}…`}
                                    emptyText={`No ${L.programmes.toLowerCase()} found.`}
                                    options={departmentProgrammes.map(p => ({ value: p.id, label: p.name }))}
                                    onChange={(v) => setProgrammeFilter(v ?? ALL)}
                                />
                            </div>
                        )}
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder={`Search ${L.classrooms.toLowerCase()}...`}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 text-sm border-border/60"
                            />
                        </div>
                        </div>
                    </CardHeader>

                    <CardContent className="p-0 relative min-h-[400px]">
                        {isLoadingList ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : listError ? (
                            <div className="flex flex-col items-center justify-center h-64 gap-2">
                                <AlertCircle className="h-8 w-8 text-red-400" />
                                <p className="text-sm text-muted-foreground">{listError}</p>
                                <Button variant="outline" size="sm" onClick={() => fetchClassrooms(true)}>Retry</Button>
                            </div>
                        ) : classrooms.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 gap-2">
                                <School className="h-10 w-10 text-muted-foreground/20" />
                                <p className="text-sm text-muted-foreground">No classrooms yet</p>
                                <Button variant="outline" size="sm" onClick={() => { setForm(emptyForm); setCreateOpen(true); }}>
                                    Create your first classroom
                                </Button>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader className="bg-muted/30">
                                    <TableRow className="border-border/60 hover:bg-transparent">
                                        {SHOW_BULK_ASSIGN && canManage && hasStructure && <TableHead className="w-8 pl-4" />}
                                        <TableHead className="pl-6 font-bold text-[11px] uppercase tracking-widest text-primary">{L.classroom}</TableHead>
                                        {hasStructure && (
                                            <TableHead className="font-bold text-[11px] uppercase tracking-widest text-primary">{L.department} / {L.programme}</TableHead>
                                        )}
                                        <TableHead className="font-bold text-[11px] uppercase tracking-widest text-primary">Level / Grade</TableHead>
                                        <TableHead className="font-bold text-[11px] uppercase tracking-widest text-primary">{L.incharge}</TableHead>
                                        <TableHead className="w-12 pr-4"><span className="sr-only">Actions</span></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {classrooms.map((cls) => (
                                        <TableRow key={cls.id} className="border-border/40 hover:bg-muted/40 transition-colors group cursor-pointer"
                                            onClick={() => { setSelectedClass(cls); setViewStudentsOpen(true); }}>
                                            {SHOW_BULK_ASSIGN && canManage && hasStructure && (
                                                <TableCell className="pl-4" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox checked={selectedIds.has(cls.id)} onCheckedChange={() => toggleSelected(cls.id)} />
                                                </TableCell>
                                            )}
                                            <TableCell className="pl-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-sm">{cls.name}</span>
                                                    {cls.programme && !hasStructure && (
                                                        <span className="text-xs text-muted-foreground italic">{cls.programme}</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            {hasStructure && (
                                                <TableCell>
                                                    {cls.departmentName ? (
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-semibold">{structure.departmentLabel(cls.departmentName)}</span>
                                                            <span className="text-xs text-muted-foreground">{structure.programmeLabel(cls.programmeId) || '—'}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-start gap-1">
                                                            <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">Unassigned</Badge>
                                                            {cls.programme && <span className="text-[11px] text-muted-foreground italic">{cls.programme}</span>}
                                                        </div>
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-sm">{cls.level || '—'}</span>
                                                    {cls.grade && (
                                                        <span className="text-[10px] text-primary uppercase font-black">{cls.grade}</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {cls.coordinator ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                                                            {cls.coordinator.name.split(' ').map(n => n[ 0 ]).join('').substring(0, 2).toUpperCase()}
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-semibold">{cls.coordinator.name}</span>
                                                            <span className="text-[10px] text-muted-foreground">{cls.coordinator.email}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground italic">Unassigned</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="w-12 pr-4 text-right">
                                                <RowActions label={cls.name} busy={deletingId === cls.id} actions={[
                                                    { label: "Students", icon: Users, onSelect: () => { setSelectedClass(cls); setViewStudentsOpen(true); } },
                                                    { label: "Registery", icon: ClipboardList, onSelect: () => { setSelectedClass(cls); setRegisteryOpen(true); } },
                                                    { label: "Timetable", icon: CalendarDays, onSelect: () => openTimetable(cls) },
                                                    { label: `Move to another ${L.department.toLowerCase()}…`, icon: MoveRight, onSelect: () => setMoveTarget(cls), hidden: !(canManage && hasStructure) },
                                                    { label: `Delete ${L.classroom.toLowerCase()}`, icon: Trash2, onSelect: () => setDeleteTarget(cls), destructive: true, hidden: !canManage },
                                                ]} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-6 py-4 border-t border-border/60 bg-muted/5">
                        <p className="text-[11px] text-muted-foreground uppercase font-bold tracking-tighter">
                            Page {currentPage}
                        </p>
                        <div className="flex gap-2">
                            <Button
                                variant="outline" size="sm"
                                className="h-8 border-border/60 text-[11px] font-bold"
                                disabled={currentPage === 1}
                                onClick={() => {
                                    setCurrentPage(p => p - 1);
                                    fetchClassrooms(true);
                                }}
                            >
                                <ChevronLeft className="h-3 w-3 mr-1" /> Prev
                            </Button>
                            <Button
                                variant="outline" size="sm"
                                className="h-8 border-border/60 text-[11px] font-bold"
                                disabled={!hasMore}
                                onClick={() => {
                                    setCurrentPage(p => p + 1);
                                    fetchClassrooms();
                                }}
                            >
                                Next <ChevronRight className="h-3 w-3 ml-1" />
                            </Button>
                        </div>
                    </div>
                </Card>

                {/* ── CREATE DIALOG ─────────────────────────────────────────── */}
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogContent className="sm:max-w-2xl border-border/60 p-0 overflow-hidden shadow-2xl">
                        <DialogHeader className="border-b border-border/40 p-6 pb-4 bg-muted/10">
                            <DialogTitle className="text-xl font-bold tracking-tight">Create New {L.classroom}</DialogTitle>
                        </DialogHeader>

                        <div className="space-y-6 py-6 px-6 max-h-[70vh] overflow-y-auto">
                            {formError && (
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                                    <AlertCircle className="h-4 w-4 shrink-0" />
                                    {formError}
                                </div>
                            )}

                            {/* 01. Identity */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">01. Identity</span>
                                    <Separator className="flex-1 opacity-50" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">
                                        {L.classroom} Name <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        placeholder="e.g. CSE Batch 2022"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="text-xs h-9 border-border/60"
                                    />
                                </div>
                                {hasStructure && (
                                    <StructurePicker
                                        structure={structure}
                                        departmentName={form.departmentName}
                                        programmeId={form.programmeId}
                                        onChange={(next) => setForm({ ...form, ...next })}
                                        requireDepartment={structure.departments.length > 0}
                                    />
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {!hasStructure && (
                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">{L.programme}</Label>
                                        <Input
                                            placeholder="e.g. B.Tech Computer Science"
                                            value={form.programme}
                                            onChange={(e) => setForm({ ...form, programme: e.target.value })}
                                            className="text-xs h-9 border-border/60"
                                        />
                                    </div>
                                    )}
                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">{L.classroom} ID</Label>
                                        <div className="h-9 flex items-center px-3 rounded-md bg-muted/30 border border-border/40 text-[10px] font-bold text-muted-foreground uppercase tracking-widest cursor-not-allowed">
                                            AUTO GENERATED
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 02. Setup */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">02. Setup</span>
                                    <Separator className="flex-1 opacity-50" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">Level</Label>
                                        <Input
                                            placeholder="e.g. Undergraduate"
                                            value={form.level}
                                            onChange={(e) => setForm({ ...form, level: e.target.value })}
                                            className="text-xs h-9"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">Grade / Year</Label>
                                        <Input
                                            placeholder="e.g. Year 3"
                                            value={form.grade}
                                            onChange={(e) => setForm({ ...form, grade: e.target.value })}
                                            className="text-xs h-9"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 03. Coordinator */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">03. {L.incharge}</span>
                                    <Separator className="flex-1 opacity-50" />
                                </div>
                                <div className="flex gap-2 p-1.5 pr-2 rounded-xl border border-dashed border-border/80 bg-muted/10 items-center">
                                    <div className="flex-1 px-3">
                                        {form.coordinator ? (
                                            <div className="flex items-center gap-2">
                                                <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                                                <div className="flex flex-col">
                                                    <span className="text-[12px] font-bold">{form.coordinator.name}</span>
                                                    <span className="text-[10px] text-muted-foreground">{form.coordinator.email}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-[11px] text-muted-foreground/60 italic">
                                                Search faculty directory...
                                            </span>
                                        )}
                                    </div>
                                    <TeacherSelectionDialog
                                        selectedName={form.coordinator?.name}
                                        onSelect={(t: any) => setForm({
                                            ...form,
                                            coordinator: {
                                                uid: t.uid,
                                                name: t.name,
                                                employee_id: t.employee_id,
                                                email: t.email,
                                                profile_pic: t.profile_pic || null,
                                            }
                                        })}
                                    />
                                </div>
                            </div>
                        </div>

                        <DialogFooter className="bg-muted/30 p-6 border-t border-border/40">
                            <DialogClose asChild>
                                <Button variant="ghost" className="text-xs font-bold uppercase tracking-widest">Discard</Button>
                            </DialogClose>
                            <Button
                                onClick={handlePublish}
                                disabled={isSubmitting}
                                className="min-w-[160px] text-xs font-bold uppercase tracking-widest shadow-lg shadow-primary/20"
                            >
                                {isSubmitting
                                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Publishing...</>
                                    : 'Publish Class'
                                }
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* ── TIMETABLE EDITOR ──────────────────────────────────────── */}
                <TimetableEditor
                    open={timetableOpen}
                    onClose={() => setTimetableOpen(false)}
                    onSave={(grid) => handleSaveTimetable(grid, false)}
                    isSaving={isSavingTT}
                    classroomName={selectedClass?.name || ''}
                    initial={timetableGrid}
                />

                {/* ── SCHEDULING CONFLICT REPORT ────────────────────────────── */}
                <TimetableConflictDialog
                    open={conflictReport !== null}
                    conflicts={conflictReport || []}
                    isSaving={isSavingTT}
                    onCancel={handleCancelConflict}
                    onProceedAnyway={handleProceedAnyway}
                />

                {/* ── DELETE CONFIRMATION ───────────────────────────────────── */}
                <AlertDialog open={!!deleteTarget} onOpenChange={(next) => !next && setDeleteTarget(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This permanently removes the classroom{deleteTarget && deleteTarget.studentCount > 0
                                    ? ` and unassigns all ${deleteTarget.studentCount} enrolled students`
                                    : ''}. This action cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                            <Button variant="destructive" onClick={confirmDelete}>Delete {L.classroom}</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* ── MOVE / BULK ASSIGN (campus structure) ──────────────────── */}
                <MoveClassroomDialog
                    classroom={moveTarget}
                    structure={structure}
                    onClose={() => setMoveTarget(null)}
                    onSaved={() => { setMoveTarget(null); fetchClassrooms(true); }}
                />
                <BulkAssignDialog
                    open={bulkOpen}
                    selectedIds={[ ...selectedIds ]}
                    structure={structure}
                    onClose={() => setBulkOpen(false)}
                    onDone={() => { setSelectedIds(new Set()); fetchClassrooms(true); }}
                />

                {/* ── STUDENT ROSTER ─────────────────────────────────────────── */}
                <ClassroomRoster
                    open={viewStudentsOpen}
                    onClose={() => setViewStudentsOpen(false)}
                    classRoomId={selectedClass?.id || null}
                    classroomName={selectedClass?.name || ''}
                    coordinatorName={selectedClass?.coordinator?.name}
                />

                {/* ── STUDENT REGISTERY — enrolment of record, drives attendance ── */}
                <ClassroomRegistery
                    open={registeryOpen}
                    onClose={() => setRegisteryOpen(false)}
                    classRoomId={selectedClass?.id || null}
                    classroomName={selectedClass?.name || ''}
                />

            </div>
        </TooltipProvider>
    );
}